import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { colored } from '../../Style.js';
import { SCIFI, metal, halo } from './style.js';

const CELL_LIFT = 0.45; // Keeps the cell clear of the emitter at the bottom of its bob
const HEAL = new THREE.Color(SCIFI.heal);

// A medical energy cell: a capped glass canister of glowing green-cyan fluid
// with a white cross on each face, hovering and turning above an emitter ring.
export class HealthPack {
  constructor(scene, healthPack) {
    this.scene = scene;
    this.healthPack = healthPack;
    this.mesh = null;
    this.cell = null;
    this.sparks = [];
    this.ownMaterials = [];
    this.bobOffset = Math.random() * Math.PI * 2; // Random starting phase for bobbing
    this.create();
  }

  own(material) {
    this.ownMaterials.push(material);
    return material;
  }

  create() {
    this.mesh = new THREE.Group();
    const groundY = -this.healthPack.position.y;

    // Emitter disc on the ground and a glowing ring marking the pickup
    const emitter = new THREE.Mesh(
      mergeGeometries([
        colored(new THREE.CylinderGeometry(0.75, 0.9, 0.16, 6), SCIFI.alloyDark, { y: groundY + 0.08 }),
        colored(new THREE.CylinderGeometry(0.45, 0.5, 0.08, 12), SCIFI.alloyLight, { y: groundY + 0.2 })
      ]),
      metal(0xffffff, { vertexColors: true })
    );
    emitter.receiveShadow = true;
    this.mesh.add(emitter);

    const ringMaterial = this.own(new THREE.MeshBasicMaterial({
      color: HEAL,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
      toneMapped: false
    }));
    this.groundRing = new THREE.Mesh(new THREE.RingGeometry(1.0, 1.18, 6, 1, Math.PI / 6), ringMaterial);
    this.groundRing.rotation.x = -Math.PI / 2;
    this.groundRing.position.y = groundY + 0.04;
    this.mesh.add(this.groundRing);

    // Beam rising from the emitter into the cell
    this.beam = new THREE.Mesh(
      new THREE.CylinderGeometry(0.28, 0.42, 1, 12, 1, true),
      this.own(new THREE.MeshBasicMaterial({
        color: HEAL, transparent: true, opacity: 0.18, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, toneMapped: false
      }))
    );
    this.mesh.add(this.beam);

    // The cell, which bobs and turns
    this.cell = new THREE.Group();
    this.cell.position.y = CELL_LIFT;
    this.mesh.add(this.cell);

    // Glowing fluid core
    this.coreMaterial = this.own(new THREE.MeshBasicMaterial({ color: HEAL.clone().multiplyScalar(1.3), toneMapped: false }));
    this.cell.add(new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.34, 0.8, 6), this.coreMaterial));

    // Glass shell
    const glass = new THREE.Mesh(new THREE.CylinderGeometry(0.46, 0.46, 0.92, 6), this.own(new THREE.MeshStandardMaterial({
      color: 0xbff8ff,
      roughness: 0.15,
      metalness: 0.2,
      transparent: true,
      opacity: 0.28,
      depthWrite: false
    })));
    glass.renderOrder = 1;
    this.cell.add(glass);

    // Metal end caps and rails
    const frame = [
      colored(new THREE.CylinderGeometry(0.5, 0.42, 0.18, 6), SCIFI.trim, { y: 0.55 }),
      colored(new THREE.CylinderGeometry(0.42, 0.5, 0.18, 6), SCIFI.trim, { y: -0.55 }),
      colored(new THREE.CylinderGeometry(0.16, 0.2, 0.12, 8), SCIFI.alloyDark, { y: 0.7 })
    ];
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2 + Math.PI / 6;
      frame.push(colored(new THREE.BoxGeometry(0.07, 1.0, 0.07), SCIFI.alloyLight, { x: Math.cos(a) * 0.47, z: Math.sin(a) * 0.47 }));
    }
    const frameMesh = new THREE.Mesh(mergeGeometries(frame), metal(0xffffff, { vertexColors: true }));
    frameMesh.castShadow = true;
    this.cell.add(frameMesh);

    // White crosses floating on both sides of the cell
    const crossMaterial = this.own(new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false }));
    const cross = mergeGeometries([
      new THREE.BoxGeometry(0.42, 0.13, 0.04).toNonIndexed(),
      new THREE.BoxGeometry(0.13, 0.42, 0.04).toNonIndexed()
    ]);
    for (const side of [-1, 1]) {
      const c = new THREE.Mesh(cross, crossMaterial);
      c.position.z = side * 0.5;
      this.cell.add(c);
    }

    // Soft halo around the cell
    this.halo = halo(HEAL, 3.2, 0.5);
    this.own(this.halo.material);
    this.mesh.add(this.halo);

    // Sparks orbiting it
    const sparkMaterial = this.own(new THREE.MeshBasicMaterial({ color: 0xc8fff0, toneMapped: false }));
    const sparkGeometry = new THREE.OctahedronGeometry(0.06, 0);
    for (let i = 0; i < 4; i++) {
      const spark = new THREE.Mesh(sparkGeometry, sparkMaterial);
      spark.userData.phase = (i / 4) * Math.PI * 2;
      this.sparks.push(spark);
      this.mesh.add(spark);
    }

    this.mesh.position.set(
      this.healthPack.position.x,
      this.healthPack.position.y,
      this.healthPack.position.z
    );
    this.groundY = groundY;

    this.scene.add(this.mesh);
  }

  // Update animation (bobbing, turning and glowing)
  update(time) {
    if (!this.cell) return;
    const t = time + this.bobOffset;

    const bob = Math.sin(time * 2 + this.bobOffset) * 0.25;
    const y = CELL_LIFT + bob;
    this.cell.position.y = y;
    this.cell.rotation.y = time * 1.2;
    this.cell.rotation.z = Math.sin(t * 1.3) * 0.1;

    // Breathing glow
    const glow = Math.sin(t * 3) * 0.5 + 0.5;
    this.coreMaterial.color.copy(HEAL).multiplyScalar(1.0 + glow * 0.6);
    this.halo.position.y = y;
    this.halo.material.opacity = 0.35 + glow * 0.3;
    this.groundRing.rotation.z = time * 0.6;
    this.groundRing.scale.setScalar(0.95 + glow * 0.1);

    // Stretch the beam from the emitter up to the bottom of the cell
    const beamBottom = this.groundY + 0.2;
    const beamTop = y - 0.5;
    this.beam.scale.y = Math.max(0.01, beamTop - beamBottom);
    this.beam.position.y = (beamTop + beamBottom) / 2;

    for (const spark of this.sparks) {
      const a = t * 1.6 + spark.userData.phase;
      spark.position.set(Math.cos(a) * 0.85, y + Math.sin(t * 2.3 + spark.userData.phase) * 0.45, Math.sin(a) * 0.85);
      spark.rotation.y = t * 3;
    }
  }

  remove() {
    if (this.mesh) {
      this.scene.remove(this.mesh);
      const geometries = new Set();
      this.mesh.traverse(child => {
        if (child.isMesh) geometries.add(child.geometry);
      });
      geometries.forEach(geometry => geometry.dispose());
      this.ownMaterials.forEach(material => material.dispose());
      this.ownMaterials = [];
      this.mesh = null;
      this.cell = null;
    }
  }
}
